import React from 'react'

import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'

import type { AboutPageContent } from './shipsticks.page.content'

/**
 * Legacy `brand.pageContent.About` shape for the shipplay brand. Retained
 * only to satisfy `brand.ts` — the live About page for shipplay is rendered
 * by `ShipplayAboutPage` (imported from `page.tsx`) and does not consume
 * this content. See `ShipplayAboutPage.tsx` for the current lift-and-shift
 * from playground.
 */
interface FiftyFiftyTextFields {
  header: string
  subHeader: string
  image: string
  body: React.ReactNode
  button?: {
    text: string
    link: { href: string }
  }
}

const commonFiftyFiftyProps = {
  backgroundColor: 'grey' as 'white' | 'grey',
  showEyebrow: false,
}

const BACKGROUND_IMAGE = '/images/shipsticks/hero-background-4.jpg'

const hero: NarrativeHeroProps = {
  backgroundImage: BACKGROUND_IMAGE,
  header: 'Created by travelers, for travelers.',
  subHeader:
    'Hate dragging bags through the airport? Us, too. That’s why we created Ship&Play, ' +
    'which sends your luggage and gear ahead so you can breeze right past baggage claim.',
}

const fiftyTexts: FiftyFiftyTextFields[] = [
  {
    header: 'Our story',
    subHeader: 'It started with too many bags and too little patience.',
    image: '/images/shipsticks/about-image-1.jpg',
    button: {
      text: 'Get started',
      link: { href: '/book/ship' },
    },
    body: (
      <>
        <p>
          Two friends were tired of the same routine on every trip: wrestling suitcases through
          crowded terminals, waiting at the carousel, and paying steep fees for the privilege. They
          wondered why getting their things to a destination had to be the hardest part of going
          anywhere. So they built a door-to-door service that ships your luggage and gear ahead, and
          launched Ship&Play in 2011. Their goal then and now:
          <b> Letting people travel hands-free.</b>
        </p>
      </>
    ),
  },
  {
    header: 'How it works',
    subHeader: 'Shipping is easy.',
    image: '/images/shipsticks/about-image-2.jpg',
    button: {
      text: 'Get the details',
      link: { href: '/how-it-works' },
    },
    body: (
      <>
        <p>
          Place your order, pack your bags, boxes, and gear as you would for any trip, and attach
          your provided shipping label. Ship&Play can pick up at your home, office, or hotel, or you
          can drop off at a local carrier. Whatever you send - suitcases, skis, clubs, or the
          kids&apos; gear - will be waiting for you at your destination.
        </p>
      </>
    ),
  },
  {
    header: 'What Sets Us Apart',
    subHeader: 'Great value and personalized service.',
    image: '/images/shipsticks/about-image-3.jpg',
    button: {
      text: 'See more benefits',
      link: { href: '/guarantee' },
    },
    body: (
      <>
        <p>
          With Ship&Play, you not only save time at the airport, you also enjoy savings passed on
          from our shipping discounts. We guarantee on-time delivery and provide complimentary
          insurance, live tracking, and customer support seven days a week - whether you&apos;re
          shipping a single suitcase or a whole family&apos;s worth of gear.
        </p>
      </>
    ),
  },
  {
    header: 'Beyond Golf Bags',
    subHeader: 'We ship. You play.',
    image: '/images/shipsticks/about-image-4.jpg',
    button: {
      text: 'Visit Ship&Play',
      link: { href: 'https://shipplay.com' },
    },
    body: (
      <>
        <p>
          Ship&Play proved how much people crave an alternative to hauling bulky luggage and
          unwieldy gear of every kind. With that in mind, we launched{' '}
          <a href={'https://shipskis.com'} className="snplay:underline">
            ShipSkis
          </a>
          ,{' '}
          <a href={'https://shipcamps.com'} className="snplay:underline">
            ShipCamps
          </a>
          , and acquired{' '}
          <a href={'https://luggagefree.com'} className="snplay:underline">
            Luggage Free
          </a>
          . Along with Ship&Play, these are all part of{' '}
          <a href={'https://shipplay.com'} className="snplay:underline">
            Ship&Play
          </a>
          , a seamless, door-to-door shipping solution for travelers worldwide.
        </p>
      </>
    ),
  },
]

const fifties: FiftyFiftyProps[] = fiftyTexts.map((text, index) => ({
  ...text,
  image: {
    src: text.image,
    alt: text.header,
  },
  layout: index % 2 === 0 ? 'image-right' : 'image-left',
  ...commonFiftyFiftyProps,
}))

const content: AboutPageContent = {
  hero,
  fifties,
}

export default content
